package com.macrofocus.eflux.network

import com.macrofocus.common.math.clampNormalize
import com.macrofocus.common.units.*
import com.macrofocus.eflux.diagram.DiagramType
import com.macrofocus.eflux.ui.PaletteType
import web.cssom.ClassName
import kotlin.math.absoluteValue
import kotlin.math.max
import kotlin.math.min
import kotlin.reflect.KClass

interface LinkField<T> {
    val name: String

    val longName: String

    val icon: String?

    val group: String

    fun getValue(row: Link): T

    fun getFormattedValue(row: Link): String? = getValue(row)?.toString()

    fun getColor(row: Link, paletteType: PaletteType): String?

    fun getClassName(diagramType: DiagramType, row: Link): ClassName?

    fun getNormalized(row: Link) : Double?

    fun getMovement(row: Link) : Double?

    override fun toString(): String

    companion object {
        val i = FunctionalLinkField<Any?>("i", "Raw", Any::class, { it.branch?.branch?.record?.i }, className = null)
        val j = FunctionalLinkField<Any?>("j", "Raw", Any::class, { it.branch?.branch?.record?.j }, className = null)
        val ckt =
            FunctionalLinkField<String?>(
                "CKT",
                "Raw",
                String::class,
                { it.branch?.branch?.record?.ckt },
                null
            )
        val r = FunctionalLinkField<Quantity<PerUnit>?>(
            "R", "Raw", Quantity::class, { it.branch?.branch?.record?.r },
            { it?.amount?.toFixed(3) + " " + it?.unit?.symbol },
            longName = "Resistance R"
        )
        val x = FunctionalLinkField<Quantity<PerUnit>?>(
            "X", "Raw", Quantity::class, { it.branch?.branch?.record?.x },
            { it?.amount?.toFixed(3) + " " + it?.unit?.symbol },
            longName = "Reactance X"
        )
        val b = FunctionalLinkField<Quantity<PerUnit>?>(
            "B",
            "Raw",
            Quantity::class,
            { it.branch?.branch?.record?.b },
            null
        )
        val ratea =
            FunctionalLinkField<Quantity<ApparentPower>?>(
                "RATEA", "Raw", Quantity::class, { it.branch?.branch?.record?.ratea },
                { it?.amount?.toFixed(0) + " " + it?.unit?.symbol },
                { it, _ ->
                    val p = it.branch?.ratea
                    when {
                        p == null -> null
                        p >= 1500.0.MegavoltAmpere -> "#980043"
                        p >= 1200.0.MegavoltAmpere -> "#dd1c77"
                        p >= 900.0.MegavoltAmpere -> "#df65b0"
                        p >= 600.0.MegavoltAmpere -> "#c994c7"
                        p >= 300.0.MegavoltAmpere -> "#d4b9da"
                        else -> "#f1eef6"
                    }
                },
                { it.branch?.branch?.record?.ratea?.let {  (min(1.0, max(-1.0, it.amount.absoluteValue / 1500.0 ))) } },
                longName = "Rated Power"
            )
        val rateb =
            FunctionalLinkField<Quantity<ApparentPower>?>(
                "RATEB", "Raw", Quantity::class, { it.branch?.branch?.record?.rateb },
                { it?.amount?.toFixed(0) + " " + it?.unit?.symbol },
                { it, _ ->
                    val p = it.branch?.rateb
                    when {
                        p == null -> null
                        p >= 1500.0.MegavoltAmpere -> "#980043"
                        p >= 1200.0.MegavoltAmpere -> "#dd1c77"
                        p >= 900.0.MegavoltAmpere -> "#df65b0"
                        p >= 600.0.MegavoltAmpere -> "#c994c7"
                        p >= 300.0.MegavoltAmpere -> "#d4b9da"
                        else -> "#f1eef6"
                    }
                },
                { it.branch?.branch?.record?.rateb?.let {  (min(1.0, max(-1.0, it.amount.absoluteValue / 1500.0 ))) } }
            )
        val ratec =
            FunctionalLinkField<Quantity<ApparentPower>?>(
                "RATEC", "Raw", Quantity::class, { it.branch?.branch?.record?.ratec },
                { it?.amount?.toFixed(0) + " " + it?.unit?.symbol },
                { it, _ ->
                    val p = it.branch?.ratec
                    when {
                        p == null -> null
                        p >= 1500.0.MegavoltAmpere -> "#980043"
                        p >= 1200.0.MegavoltAmpere -> "#dd1c77"
                        p >= 900.0.MegavoltAmpere -> "#df65b0"
                        p >= 600.0.MegavoltAmpere -> "#c994c7"
                        p >= 300.0.MegavoltAmpere -> "#d4b9da"
                        else -> "#f1eef6"
                    }
                },
                { it.branch?.branch?.record?.ratec?.let {  (min(1.0, max(-1.0, it.amount.absoluteValue / 1500.0 ))) } }
            )
        val gi = FunctionalLinkField<Quantity<PerUnit>?>(
            "GI",
            "Raw",
            Quantity::class,
            { it.branch?.branch?.record?.gi },
            null
        )
        val bi = FunctionalLinkField<Quantity<PerUnit>?>(
            "BI",
            "Raw",
            Quantity::class,
            { it.branch?.branch?.record?.bi },
            null
        )
        val gj = FunctionalLinkField<Quantity<PerUnit>?>(
            "GJ",
            "Raw",
            Double::class,
            { it.branch?.branch?.record?.gj },
            null
        )
        val bj = FunctionalLinkField<Quantity<PerUnit>?>(
            "BJ",
            "Raw",
            Double::class,
            { it.branch?.branch?.record?.bj },
            null
        )
        val st = FunctionalLinkField<Int?>(
            "ST",
            "Raw",
            Int::class,
            { it.branch?.branch?.record?.st },
            null,
            longName = "Status"
        )
        val met = FunctionalLinkField<Int?>(
            "MET",
            "Raw",
            Int::class,
            { it.branch?.branch?.record?.met },
            null
        )
        val len =
            FunctionalLinkField<Double?>(
                "LEN",
                "Raw",
                Double::class,
                { it.branch?.branch?.record?.len },
                null
            )
        val o1 = FunctionalLinkField<Double?>(
            "O1",
            "Raw",
            Double::class,
            { it.branch?.branch?.record?.o1 },
            null
        )
        val f1 = FunctionalLinkField<Double?>(
            "F1",
            "Raw",
            Double::class,
            { it.branch?.branch?.record?.f1 },
            null
        )
//        val o2 = FunctionalLinkField("o1", Double::class, { it.branch?.branch?.record?.o2 })
//        val f2 = FunctionalLinkField("f1", Double::class, { it.branch?.branch?.record?.f2 })

        val baskv = FunctionalLinkField<Quantity<Voltage>?>(
            "BASKV", "Derived", Quantity::class, { it.branch?.baskv },
            null,
            { it, paletteType ->
                val voltage = it.branch?.baskv
                when {
                    voltage == null -> null
                    voltage >= 380.Kilovolt -> if(paletteType == PaletteType.dark) "#835eac" else "#7346A2"
                    voltage >= 220.Kilovolt -> if(paletteType == PaletteType.dark) "#268d05" else "#17AF97"
                    else -> if(paletteType == PaletteType.dark) "#2a9cf4" else "#0FB2E5"
                }
            },
            { it.branch?.baskv?.let {  min(1.0, it / 380.Kilovolt) } },
            longName = "Voltage level",
            className = { diagramType, it ->
//                if(diagramType == DiagramType.Tube) {
//                    val status = it.branch?.branch?.record?.st
//                    if(status != 1) {
//                        val voltage = it.branch?.baskv
//                        when {
//                            voltage == null -> null
//                            voltage >= 380.Kilovolt -> "link-380kv-outofservice"
//                            voltage >= 220.Kilovolt -> "link-220kv-outofservice"
//                            voltage >= 150.Kilovolt -> "link-150kv-outofservice"
//                            else -> ""
//                        }
//                    } else {
                        val voltage = it.branch?.baskv
                        when {
                            voltage == null -> null
                            voltage >= 380.Kilovolt -> ClassName("kv380")
                            voltage >= 220.Kilovolt -> ClassName("kv220")
                            voltage >= 150.Kilovolt -> ClassName("kv150")
                            else -> ClassName("")
                        }
//                    }
//                } else {
//                    null
//                }
            }
        )
        val vm =
            FunctionalLinkField<Quantity<PerUnit>?>(
                "VM", "Derived", Quantity::class, { it.branch?.vm },
                { it?.amount?.toFixed(4) + "" + it?.unit?.symbol },
                { it, _ ->
                    val va = it.branch?.vm
                    when {
                        va == null -> null
                        va > 0.pu -> "#fc8d59"
                        va == 0.pu -> "#ffffbf"
                        else -> "#91bfdb"
                    }
                },
                { it.branch?.vm_m?.let { it.amount.clampNormalize(0.95, 1.19) } },
                longName = "Voltage Magnitude"
            )
        val dvm =
            FunctionalLinkField<Quantity<PerUnit>?>(
                "ΔVM", "Derived", Quantity::class, { it.branch?.dvm },
                { it?.amount?.toFixed(4) + "" + it?.unit?.symbol },
                { it, _ ->
                    val va = it.branch?.dvm
                    when {
                        va == null -> null
                        va > 0.pu -> "#fc8d59"
                        va == 0.pu -> "#ffffbf"
                        else -> "#91bfdb"
                    }
                },
                { it.branch?.vm_m?.let { it.amount.clampNormalize(0.95, 1.19) } },
                longName = "Δ[Voltage Magnitude]"
            )
        val va =
            FunctionalLinkField<Quantity<PhaseAngle>?>(
                "VA", "Derived", Quantity::class, { it.branch?.va },
                { it?.amount?.toFixed(2) + "" + it?.unit?.symbol },
                { it, _ ->
                    val va = it.branch?.va
                    when {
                        va == null -> null
                        va > 0.PhaseDegree -> "#fc8d59"
                        va == 0.PhaseDegree -> "#ffffbf"
                        else -> "#91bfdb"
                    }
                },
                { it.branch?.va?.let {  it.amount.clampNormalize(-30.0, +30.0) } },
                longName = "Voltage Angle"
            )
        val dva =
            FunctionalLinkField<Quantity<PhaseAngle>?>(
                "ΔVA", "Derived", Quantity::class, { it.branch?.dva },
                { it?.amount?.toFixed(2) + "" + it?.unit?.symbol },
                { it, _ ->
                    val va = it.branch?.dva
                    when {
                        va == null -> null
                        va > 0.PhaseDegree -> "#fc8d59"
                        va == 0.PhaseDegree -> "#ffffbf"
                        else -> "#91bfdb"
                    }
                },
                { it.branch?.dva?.let {  it.amount.clampNormalize(-30.0, +30.0) } },
                longName = "Δ[Voltage Angle]"
            )
        val p = FunctionalLinkField<Quantity<Power>?>(
            "P", "Derived", Quantity::class, { it.branch?.p },
            { it?.amount?.toFixed(0) + " " + it?.unit?.symbol },
            { it, _ ->
                val p = it.branch?.p
                when {
                    p == null -> null
                    p >= 1000.0.Megawatt -> "#980043"
                    p >= 800.0.Megawatt -> "#dd1c77"
                    p >= 600.0.Megawatt -> "#df65b0"
                    p >= 400.0.Megawatt -> "#c994c7"
                    p >= 200.0.Megawatt -> "#d4b9da"
                    else -> "#f1eef6"
                }
            },
            { it.branch?.p?.let {  (min(1.0, max(-0.0, it.amount.absoluteValue / 1000.0 ))) } },
            { it.branch?.p?.let {  (min(1.0, max(-0.0, it.amount.absoluteValue / 1000.0 ))) } },
            longName = "Power P"
        )
        val q = FunctionalLinkField<Quantity<ReactivePower>?>(
            "Q", "Derived", Quantity::class, { it.branch?.q },
            { it?.amount?.toFixed(0) + " " + it?.unit?.symbol },
            { it, _ ->
                val p = it.branch?.q
                when {
                    p == null -> null
                    p >= 1000.0.MegavoltAmpereReactive -> "#980043"
                    p >= 800.0.MegavoltAmpereReactive -> "#dd1c77"
                    p >= 600.0.MegavoltAmpereReactive -> "#df65b0"
                    p >= 400.0.MegavoltAmpereReactive -> "#c994c7"
                    p >= 200.0.MegavoltAmpereReactive -> "#d4b9da"
                    else -> "#f1eef6"
                }
            },
            { it.branch?.q?.let {  (min(1.0, max(-0.0, it.amount.absoluteValue / 1000.0 ))) } },
            { it.branch?.q?.let {  (min(1.0, max(-0.0, it.amount.absoluteValue / 1000.0 ))) } },
            longName = "Power Q"
        )
        val abs_s = FunctionalLinkField<Quantity<ApparentPower>?>(
            "ABS(S)", "Derived", Quantity::class, { it.branch?.abs_s },
            { it?.amount?.toFixed(0) + " " + it?.unit?.symbol },
            { it, _ ->
                val p = it.branch?.abs_s
                when {
                    p == null -> null
                    p >= 1000.0.MegavoltAmpere -> "#980043"
                    p >= 800.0.MegavoltAmpere -> "#dd1c77"
                    p >= 600.0.MegavoltAmpere -> "#df65b0"
                    p >= 400.0.MegavoltAmpere -> "#c994c7"
                    p >= 200.0.MegavoltAmpere -> "#d4b9da"
                    else -> "#f1eef6"
                }
            },
            { it.branch?.abs_s?.let {  (min(1.0, max(-0.0, it.amount.absoluteValue / 1000.0 ))) } },
            { it.branch?.abs_s?.let {  (min(1.0, max(-0.0, it.amount.absoluteValue / 1000.0 ))) } },
            longName = "Power abs(S)"
        )

        val pratea = FunctionalLinkField<Double?>(
            "P/RATEA", "Derived", Double::class, { it.branch?.pratea },
            { it?.times(100.0)?.toFixed(1) + "%"},
            { it, _ ->
                val p = it.branch?.pratea
                when {
                    p == null -> null
                    p >= 0.8 -> "#980043"
                    p >= 0.6 -> "#dd1c77"
                    p >= 0.4 -> "#df65b0"
                    p >= 0.2 -> "#c994c7"
                    p >= 0.0 -> "#d4b9da"
                    else -> "#f1eef6"
                }
            },
            {
                val branch = it.branch
                if(branch != null) {
                    if (branch.p >= 2.Megawatt) {
                        branch.pratea?.let { (min(1.0, max(0.0, it))) }
                    } else {
                        // Below 2 MW there should be no animation
                        0.0
                    }
                } else {
                    null
                }
            },
            longName = "P / [Rated Power]"
        )

        val onepratea = FunctionalLinkField<Double?>(
            "1-(P/RATEA)", "Derived", Double::class, { it.branch?.onepratea },
            { it?.times(100.0)?.toFixed(1) + "%"},
            { it, paletteType ->
                val p = it.branch?.onepratea
                when {
                    p == null -> null
                    p >= 0.8 -> "#980043"
                    p >= 0.6 -> "#dd1c77"
                    p >= 0.4 -> "#df65b0"
                    p >= 0.2 -> "#c994c7"
                    p >= 0.0 -> "#d4b9da"
                    else -> "#f1eef6"
                }
            },
            { it.branch?.onepratea?.let {  (min(1.0, max(0.0, it ))) } },
            longName = "1 – P / [Rated Power]"
        )

        val sratea = FunctionalLinkField<Double?>(
            "ABS(S)/RATEA", "Derived", Double::class, { it.branch?.sratea },
            { it?.times(100.0)?.toFixed(1) + "%"},
            { it, paletteType ->
                val p = it.branch?.sratea
                when {
                    p == null -> null
                    p >= 0.8 -> "#980043"
                    p >= 0.6 -> "#dd1c77"
                    p >= 0.4 -> "#df65b0"
                    p >= 0.2 -> "#c994c7"
                    p >= 0.0 -> "#d4b9da"
                    else -> "#f1eef6"
                }
            },
            { it.branch?.sratea?.let {  (min(1.0, max(0.0, it ))) } },
            longName = "abs(S) / [Rated Power]"
        )

        val vm_from = FunctionalLinkField(
            "U_FROM",
            "Metered From",
            Double::class,
            { it.branch?.vm_k },
            { it?.amount?.toFixed(4) + "" + it?.unit?.symbol },
            { _, _ -> null },
            { it.branch?.vm_k?.let { it.amount.clampNormalize(0.95, 1.19) } },
            { null },
            "text_fields_icon",
            "[Voltage Magnitude."
        )

        val vm_to = FunctionalLinkField(
            "U_TO",
            "Metered To",
            Double::class,
            { it.branch?.vm_m },
            { it?.amount?.toFixed(4) + "" + it?.unit?.symbol },
            null,
            { it.branch?.vm_m?.let { it.amount.clampNormalize(0.95, 1.19) } },
            { null },
            "text_fields_icon",
            ".Voltage Magnitude]"
        )

        val va_from = FunctionalLinkField(
            "VA_FROM",
            "Metered From",
            Double::class,
            { it.branch?.va_k },
            { it?.amount?.toFixed(2) + "" + it?.unit?.symbol },
            { _, _ -> null },
            { it.branch?.va_k?.let { it.amount.clampNormalize(-30.0, +30.0) } },
            { null },
            "text_fields_icon",
            "[Voltage Angle."
        )

        val va_to = FunctionalLinkField(
            "VA_TO",
            "Metered To",
            Double::class,
            { it.branch?.va_m },
            { it?.amount?.toFixed(2) + "" + it?.unit?.symbol },
            { _, _ -> null },
            { it.branch?.va_m?.let {  it.amount.clampNormalize(-30.0, +30.0) } },
            { null },
            "text_fields_icon",
            ".Voltage Angle]"
        )

        val all: Array<LinkField<*>> = arrayOf(
            i,
            j,
            ckt,
            r,
            x,
            b,
            ratea,
            rateb,
            ratec,
            gi,
            bi,
            gj,
            bj,
            st,
            met,
            len,
            o1,
            f1,
            dvm,
            dva,
            p, q, abs_s,
            pratea,
            onepratea,
            sratea
        )
        val tooltip: Array<LinkField<*>> = arrayOf(
//            ckt,
            r, x, b, ratea, rateb, ratec, gi, bi, gj, bj, st, met,
//            len, o1, f1,
            baskv, dvm, dva, p, q, abs_s, pratea, onepratea, sratea, va_from, va_to
//            from_name, from_baskv, from_ide, from_area, from_zone, from_owner, from_vm, from_va, from_nvhi, from_nvlo, from_evhi, from_evlo,
//            to_name, to_baskv, to_ide, to_area, to_zone, to_owner, to_vm, to_va, to_nvhi, to_nvlo, to_evhi, to_evlo
            //                                BranchPowerFlowRawColumn("o2", Double::class, { it.o2 }),
            //                                BranchPowerFlowRawColumn("f2", Double::class, { it.f2 })
        )

        val thickness: Array<LinkField<*>> = arrayOf(
//            ckt,
//            r, x, b,
//            ratea, rateb, ratec,
//            gi, bi, gj, bj, st, met,
//            len, o1, f1,
//            baskv, va,
            p,
            pratea,
//            onepratea, sratea
//            from_name, from_baskv, from_ide, from_area, from_zone, from_owner, from_vm, from_va, from_nvhi, from_nvlo, from_evhi, from_evlo,
//            to_name, to_baskv, to_ide, to_area, to_zone, to_owner, to_vm, to_va, to_nvhi, to_nvlo, to_evhi, to_evlo
            //                                BranchPowerFlowRawColumn("o2", Double::class, { it.o2 }),
            //                                BranchPowerFlowRawColumn("f2", Double::class, { it.f2 })
        )

        val color: Array<LinkField<*>> = arrayOf(
//            ckt,
//            r, x, b,
//            ratea,
//            rateb, ratec,
//            gi, bi, gj, bj, st, met,
//            len, o1, f1,
            baskv,
//            dva, p, pratea, onepratea
//            from_name, from_baskv, from_ide, from_area, from_zone, from_owner, from_vm, from_va, from_nvhi, from_nvlo, from_evhi, from_evlo,
//            to_name, to_baskv, to_ide, to_area, to_zone, to_owner, to_vm, to_va, to_nvhi, to_nvlo, to_evhi, to_evlo
            //                                BranchPowerFlowRawColumn("o2", Double::class, { it.o2 }),
            //                                BranchPowerFlowRawColumn("f2", Double::class, { it.f2 })
        )

        val heatmap: Array<LinkField<*>> = arrayOf(
//            ckt,
//            r, x, b,
//            ratea,
//            rateb, ratec,
//            gi, bi, gj, bj, st, met,
//            len, o1, f1,
//            baskv, vm, dvm, va,
            dva,
//            p,
            pratea
//            , onepratea
//            from_name, from_baskv, from_ide, from_area, from_zone, from_owner, from_vm, from_va, from_nvhi, from_nvlo, from_evhi, from_evlo,
//            to_name, to_baskv, to_ide, to_area, to_zone, to_owner, to_vm, to_va, to_nvhi, to_nvlo, to_evhi, to_evlo
            //                                BranchPowerFlowRawColumn("o2", Double::class, { it.o2 }),
            //                                BranchPowerFlowRawColumn("f2", Double::class, { it.f2 })
        )

        val movement: Array<LinkField<*>> = arrayOf(
//            ckt,
//            r, x, b,
//            ratea, rateb, ratec,
//            gi, bi, gj, bj, st, met,
//            len, o1, f1,
//            va, p,
            pratea
//            from_name, from_baskv, from_ide, from_area, from_zone, from_owner, from_vm, from_va, from_nvhi, from_nvlo, from_evhi, from_evlo,
//            to_name, to_baskv, to_ide, to_area, to_zone, to_owner, to_vm, to_va, to_nvhi, to_nvlo, to_evhi, to_evlo
            //                                BranchPowerFlowRawColumn("o2", Double::class, { it.o2 }),
            //                                BranchPowerFlowRawColumn("f2", Double::class, { it.f2 })
        )

        val selected: Array<LinkField<*>> = arrayOf(
            st, baskv, p, q, ratea, pratea,
//            r, x
//            va, p, ratea, va_from, va_to, x
            //                                BranchPowerFlowRawColumn("o2", Double::class, { it.o2 }),
            //                                BranchPowerFlowRawColumn("f2", Double::class, { it.f2 })
        )

    }
}

class FunctionalLinkField<T>(
    override val name: String,
    override val group: String,
    private val type: KClass<*>,
    private val value: (Link) -> T,
    private val formattedValue: ((T) -> String?)? = null,
    private val color: ((Link, PaletteType) -> String?)? = null,
    private val normalized: ((Link) -> Double?)? = null,
    private val movement: ((Link) -> Double?)? = normalized,
    override val icon: String = "link_icon",
    override val longName: String = name,
    val className: ((DiagramType, Link) -> ClassName?)? = null
) : LinkField<T> {
    override fun getValue(row: Link): T {
        return value.invoke(row)
    }

    override fun getFormattedValue(row: Link): String? {
        if(formattedValue != null) {
            val value : T = getValue(row)
            return formattedValue.invoke(value)
        } else {
            return super.getFormattedValue(row)
        }
    }

    override fun getColor(row: Link, paletteType: PaletteType): String? {
        return color?.invoke(row, paletteType)
    }

    override fun getClassName(diagramType: DiagramType, row: Link): ClassName? {
        return className?.invoke(diagramType, row)
    }

    override fun getNormalized(row: Link): Double? {
        return normalized?.invoke(row)
    }

    override fun getMovement(row: Link): Double? {
        return movement?.invoke(row)
    }

    override fun toString(): String {
        return longName
    }
}